import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout as DashboardLayout } from 'src/layouts/dashboard';

const OverviewPage = lazy(() => import('src/pages/dashboard/index'));
const RequestCarrierListPage = lazy(
  () => import('src/pages/dashboard/request-carrier/index'),
);
const ChangeRoleCarrierPage = lazy(
  () => import('src/pages/dashboard/change-role-carrier/index'),
);
const DispatcherListPage = lazy(
  () => import('src/pages/dashboard/dispatcher/index'),
);
const DriverListPage = lazy(() => import('src/pages/dashboard/driver/index'));
const TruckListPage = lazy(() => import('src/pages/dashboard/truck/index'));
const AddTruckPage = lazy(
  () => import('src/pages/dashboard/truck/create/index'),
);
const TruckReportPage = lazy(() => import('src/pages/dashboard/truck/report'));
const DriverReportPage = lazy(
  () => import('src/pages/dashboard/report/driver/index'),
);
const DispatcherReportPage = lazy(
  () => import('src/pages/dashboard/report/dispatcher/index'),
);
const CarrierListPage = lazy(() => import('src/pages/dashboard/carrier/index'));
const AddCarrierPage = lazy(
  () => import('src/pages/dashboard/carrier/create/index'),
);
const TransactionListPage = lazy(
  () => import('src/pages/dashboard/transactions/index'),
);
const GiftTransactionListPage = lazy(
  () => import('src/pages/dashboard/gift-transactions'),
);
const TripListPage = lazy(() => import('src/pages/dashboard/trip/index'));
const TripCreatePage = lazy(() => import('src/pages/dashboard/trip/create'));
const TripTrackingDetailPage = lazy(
  () => import('src/pages/dashboard/trip/tracking/index'),
);
const LoadListPage = lazy(() => import('src/pages/dashboard/load/index'));
const LoadManagmentListPage = lazy(
  () => import('src/pages/dashboard/load-management/index'),
);
const LoadCreatePage = lazy(() => import('src/pages/dashboard/load/create'));
const UserListPage = lazy(() => import('src/pages/dashboard/report/users'));
const CarriersActivityListPage = lazy(
  () => import('src/pages/dashboard/report/carriers'),
);
const HelperListPage = lazy(() => import('src/pages/dashboard/helper/index'));
const CreateHelperPage = lazy(
  () => import('src/pages/dashboard/helper/create'),
);

const SliderListPage = lazy(() => import('src/pages/dashboard/slider/index'));

const CreateSliderPage = lazy(
  () => import('src/pages/dashboard/slider/create'),
);
const DriverDetailPage = lazy(
  () => import('src/pages/dashboard/driver/detail'),
);
const DriverCreatePage = lazy(
  () => import('src/pages/dashboard/driver/create'),
);
const DispatcherCreatePage = lazy(
  () => import('src/pages/dashboard/dispatcher/create'),
);
const DispatcherDetailPage = lazy(
  () => import('src/pages/dashboard/dispatcher/detail'),
);

const TruckVisualPage = lazy(
  () => import('src/pages/dashboard/truck-visual/index'),
);
const CalculatorPage = lazy(
  () => import('src/pages/dashboard/calculator/index'),
);
const PlansPage = lazy(() => import('src/pages/dashboard/plans/index'));
const SelectUnlockItemsPage = lazy(
  () => import('src/pages/dashboard/select-unlock-items/index'),
);
const PaymentPage = lazy(
  () => import('src/pages/dashboard/payment/payment-page'),
);
const AdminSettingPage = lazy(
  () => import('src/pages/dashboard/admin-setting/index'),
);
const ReferralCodeListPage = lazy(
  () => import('src/pages/dashboard/referral-code'),
);
const ReferralCodeCreatePage = lazy(
  () => import('src/pages/dashboard/referral-code/create'),
);
const DiscountListPage = lazy(
  () => import('src/pages/dashboard/discount/index'),
);
const DiscountCreatePage = lazy(
  () => import('src/pages/dashboard/discount/create'),
);
const SubscriptionPlanListPage = lazy(
  () => import('src/pages/dashboard/subscription-plans/index'),
);
const SubscriptionPlanCreatePage = lazy(
  () => import('src/pages/dashboard/subscription-plans/create'),
);
const WithdrawRequestListPage = lazy(
  () => import('src/pages/dashboard/withdraw-request-list/index'),
);
const BrokerLoadListPage = lazy(
  () => import('src/pages/dashboard/broker-load/index'),
);
const BrokerLoadAwaitingQuotePage = lazy(
  () => import('src/pages/dashboard/broker-load/awaiting-quote'),
);
const BrokerLoadDetailPage = lazy(
  () => import('src/pages/dashboard/broker-load/detail'),
);
const BrokerLoadCarrierOffersPage = lazy(
  () => import('src/pages/dashboard/broker-load/carrier-offers'),
);
const BrokerLoadGmailThreadPage = lazy(
  () => import('src/pages/dashboard/broker-load/gmail-thread'),
);
const BrokerLoadTrackingPage = lazy(
  () => import('src/pages/dashboard/broker-load/tracking'),
);
const BrokerLoadWorkflowConfigPage = lazy(
  () => import('src/pages/dashboard/broker-load/workflow-config'),
);
const BrokerLoadCheckpointsPage = lazy(
  () => import('src/pages/dashboard/broker-load/checkpoints'),
);
const BrokerLoadCheckpointDetailPage = lazy(
  () => import('src/pages/dashboard/broker-load/checkpoint-detail'),
);
const CarrierLanesPage = lazy(
  () => import('src/pages/dashboard/carrier-lanes/index'),
);
const ShippersPage = lazy(() => import('src/pages/dashboard/shippers/index'));
const BrokerEmailsPage = lazy(() => import('src/pages/dashboard/emails/index'));
const MailboxPage = lazy(() => import('src/pages/dashboard/mailbox/index'));
const MailboxThreadPage = lazy(
  () => import('src/pages/dashboard/mailbox/thread/index'),
);
const CarrierProfilesListPage = lazy(
  () => import('src/pages/dashboard/broker-profiles/carriers/index'),
);
const CarrierProfileDetailPage = lazy(
  () => import('src/pages/dashboard/broker-profiles/carriers/detail'),
);
const ShipperProfilesListPage = lazy(
  () => import('src/pages/dashboard/broker-profiles/shippers/index'),
);
const ShipperProfileDetailPage = lazy(
  () => import('src/pages/dashboard/broker-profiles/shippers/detail'),
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: '',
    element: (
      <DashboardLayout>
        <Suspense>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        element: <OverviewPage />,
      },
      // {
      //   path: 'Load',
      //   children: [
      //     {
      //       path: 'create',
      //       element: <LoadCreatePage />,
      //     },
      //   ],
      // },

      {
        path: '',
        children: [
          {
            path: 'requests',
            element: <RequestCarrierListPage />,
          },
          {
            path: 'broker-loads',
            children: [
              {
                index: true,
                element: <BrokerLoadListPage />,
              },
              {
                path: 'awaiting-quote',
                element: <BrokerLoadAwaitingQuotePage />,
              },
              {
                path: 'workflow-config',
                element: <BrokerLoadWorkflowConfigPage />,
              },
              {
                path: 'checkpoints',
                element: <BrokerLoadCheckpointsPage />,
              },
              {
                path: 'checkpoints/:checkpointId',
                element: <BrokerLoadCheckpointDetailPage />,
              },
              {
                path: ':loadId/tracking',
                element: <BrokerLoadTrackingPage />,
              },
              {
                path: 'load-temp/:loadTempId/gmail-threads/:threadId',
                element: <BrokerLoadGmailThreadPage />,
              },
              {
                path: 'load-temp/:loadTempId',
                element: <BrokerLoadDetailPage />,
              },
              {
                path: ':loadId',
                element: <BrokerLoadDetailPage />,
              },
              {
                path: ':loadId/carrier-offers',
                element: <BrokerLoadCarrierOffersPage />,
              },
              {
                path: ':loadId/gmail-threads/:threadId',
                element: <BrokerLoadGmailThreadPage />,
              },
            ],
          },
          {
            path: 'carrier-lanes',
            element: <CarrierLanesPage />,
          },
          {
            path: 'shipper',
            element: <ShippersPage />,
          },
          {
            path: 'my-emails',
            element: <BrokerEmailsPage />,
          },
          {
            path: 'mail-box',
            element: <MailboxPage />,
          },
          {
            path: 'mail-box/thread/:threadId',
            element: <MailboxThreadPage />,
          },
          {
            path: 'broker',
            children: [
              {
                path: 'carriers',
                element: <CarrierProfilesListPage />,
              },
              {
                path: 'carriers/:carrierId',
                element: <CarrierProfileDetailPage />,
              },
              {
                path: 'shippers',
                element: <ShipperProfilesListPage />,
              },
              {
                path: 'shippers/:shipperId',
                element: <ShipperProfileDetailPage />,
              },
            ],
          },
          {
            path: 'dispatchers',
            children: [
              {
                index: true,
                element: <DispatcherListPage />,
              },
              {
                path: ':dispatcherId',
                element: <DispatcherDetailPage />,
              },
              {
                path: 'create',
                element: <DispatcherCreatePage />,
              },
            ],
          },
          {
            path: 'drivers',
            children: [
              {
                index: true,
                element: <DriverListPage />,
              },
              {
                path: ':driverId',
                element: <DriverDetailPage />,
              },
              {
                path: 'create',
                element: <DriverCreatePage />,
              },
            ],
          },
          {
            path: 'loads',
            children: [
              {
                index: true,
                element: <LoadListPage />,
              },
              {
                path: 'create',
                element: <LoadCreatePage />,
              },
              {
                path: 'create/:loadId',
                element: <LoadCreatePage />,
              },
            ],
          },
          {
            path: 'load-management',
            children: [
              {
                path: ':tripId',
                element: <LoadManagmentListPage />,
              },
            ],
          },
          {
            path: 'trucks',
            children: [
              {
                index: true,
                element: <TruckListPage />,
              },
              {
                path: 'create',
                element: <AddTruckPage />,
              },
              {
                path: 'report/:truckId',
                element: <TruckReportPage />,
              },
            ],
          },
          {
            path: 'carriers',
            children: [
              {
                index: true,
                element: <CarrierListPage />,
              },
              {
                path: 'create',
                element: <AddCarrierPage />,
              },
            ],
          },

          {
            path: 'users',
            children: [
              {
                index: true,
                element: <CarrierListPage />,
              },
            ],
          },
          {
            path: 'calculator',
            children: [
              {
                index: true,
                element: <CalculatorPage />,
              },
            ],
          },
          {
            path: 'truck-visual',
            children: [
              {
                index: true,
                element: <TruckVisualPage />,
              },
            ],
          },
          {
            path: 'trips',
            children: [
              {
                index: true,
                element: <TripListPage />,
              },
              {
                path: 'create',
                element: <TripCreatePage />,
              },
              {
                path: 'create/:tripId',
                element: <TripCreatePage />,
              },
              {
                path: 'tracking/:tripId',
                element: <TripTrackingDetailPage />,
              },
            ],
          },
          {
            path: 'report',
            children: [
              {
                path: 'drivers-activity',
                element: <DriverReportPage />,
              },
              {
                path: 'dispatchers-activity',
                element: <DispatcherReportPage />,
              },
              {
                path: 'users',
                element: <UserListPage />,
              },
              {
                path: 'carriers-activity',
                element: <CarriersActivityListPage />,
              },
              {
                path: 'transactions',
                children: [
                  {
                    index: true,
                    element: <TransactionListPage />,
                  },
                ],
              },
              {
                path: 'withdraw-requests',
                element: <WithdrawRequestListPage />,
              },
              {
                path: 'gift/transactions',
                element: <GiftTransactionListPage />,
              },
            ],
          },
          {
            path: 'referral-code',
            children: [
              {
                index: true,
                element: <ReferralCodeListPage />,
              },
              {
                path: 'create',
                element: <ReferralCodeCreatePage />,
              },
            ],
          },
          {
            path: 'discount',
            children: [
              {
                index: true,
                element: <DiscountListPage />,
              },
              {
                path: 'create',
                element: <DiscountCreatePage />,
              },
            ],
          },
          {
            path: 'settings/subscription-plans',
            children: [
              {
                index: true,
                element: <SubscriptionPlanListPage />,
              },
              {
                path: 'create',
                element: <SubscriptionPlanCreatePage />,
              },
            ],
          },
          {
            path: 'content',
            children: [
              {
                path: 'helpers',
                element: <HelperListPage />,
              },
              {
                path: 'helpers/create',
                element: <CreateHelperPage />,
              },
              {
                path: 'helpers/create/:helperId',
                element: <CreateHelperPage />,
              },
              {
                path: 'sliders',
                element: <SliderListPage />,
              },
              {
                path: 'sliders/create',
                element: <CreateSliderPage />,
              },
              {
                path: 'sliders/create/:sliderId',
                element: <CreateSliderPage />,
              },
            ],
          },
        ],
      },

      {
        path: 'change-role-carrier',
        element: <ChangeRoleCarrierPage />,
      },
      {
        path: 'subscription-plan',
        element: <PlansPage />,
      },
      {
        path: 'select-unlock-items',
        element: <SelectUnlockItemsPage />,
      },
      {
        path: 'payment',
        element: <PaymentPage />,
      },
      {
        path: 'settings/system-setting',
        element: <AdminSettingPage />,
      },
    ],
  },
];
